import { TZDate } from '@date-fns/tz';
import { differenceInYears, format, isValid, parseISO } from 'date-fns';

/**
 * Exactly `YYYY-MM-DD`. Anything else is rejected rather than coerced —
 * see {@link isoToDate} for why the shape check has to come first.
 */
const ISO_DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Parses an ISO date-only string (`YYYY-MM-DD`) as LOCAL midnight.
 *
 * `new Date('1994-03-15')` parses as UTC midnight, which renders as March 14
 * in any negative-offset (US) timezone — an off-by-one date of birth. Always
 * use this helper for date-only values.
 *
 * The shape is checked BEFORE parsing, because `parseISO` accepts the whole of
 * ISO-8601 and `isValid` only asks whether the *result* is a real date — not
 * whether the *input* was date-only. Without the guard, under a negative
 * offset:
 *
 *   '1994-03-15T02:30:00Z' -> 1994-03-14  (the very bug this helper prevents)
 *   '1994-03'              -> 1994-03-01  (fabricates a day)
 *   '1994'                 -> 1994-01-01  (fabricates a month and a day)
 *
 * Those are reachable: `date_of_birth` is an unvalidated string end to end, and
 * the schedule-upload importer stores whatever it was given when its expected
 * `MM/DD/YYYY` parse fails.
 *
 * @param value - ISO date-only string, or '' when unset
 * @returns The local Date, or undefined when empty, misshapen, or not a real date
 *
 * @example
 * isoToDate('1994-03-15');           // local midnight, March 15 1994
 * isoToDate('1994-03-15T02:30:00Z'); // undefined — not date-only
 * isoToDate('1994-02-30');           // undefined — not a real date
 */
export const isoToDate = (value: string): Date | undefined => {
  if (!ISO_DATE_ONLY.test(value)) return undefined;

  const parsed = parseISO(value);
  return isValid(parsed) ? parsed : undefined;
};

/**
 * Formats a Date as an ISO date-only string (`YYYY-MM-DD`) in LOCAL time.
 * The inverse of {@link isoToDate}.
 *
 * @example
 * dateToIso(new Date(1994, 2, 15)); // "1994-03-15"
 */
export const dateToIso = (date: Date): string => format(date, 'yyyy-MM-dd');

/**
 * Whole years elapsed from an ISO date-only birth date to today.
 *
 * Goes through {@link isoToDate}, so it cannot be thrown off by the UTC-midnight
 * parse that makes `new Date('1994-03-15')` land a day early.
 *
 * @param value - ISO date-only string; null/undefined/malformed yields undefined
 *
 * @example
 * ageFromIso('1994-03-15'); // 32
 * ageFromIso(undefined);    // undefined
 */
export const ageFromIso = (value: string | null | undefined): number | undefined => {
  const born = value ? isoToDate(value) : undefined;
  return born ? differenceInYears(new Date(), born) : undefined;
};

/**
 * Formats an INSTANT — a moment in time — in the given zone.
 *
 * Use for `created_at`, `generated_at`, `expires_at`: values that answer "when
 * did this happen". For `date_of_birth`, `encounter_date`, `document_date` and
 * other calendar dates use {@link formatCalendarDate}, which takes no zone
 * because those values do not have one — that asymmetry (instants convert to a
 * zone, calendar dates have none to convert to) is why the two functions exist
 * separately.
 */
export const formatInstant = (iso: string, pattern: string, timeZone: string): string => {
  const instant = new Date(iso);
  if (Number.isNaN(instant.getTime())) return iso;

  return format(new TZDate(instant, timeZone), pattern);
};

/**
 * Formats a CALENDAR DATE — a day on a calendar, with no time and no zone.
 *
 * Takes no `timeZone` parameter, deliberately. A date of birth is March 15 in
 * Karachi and in Ohio; offering a zone here is what produces the off-by-one
 * this function exists to prevent. Goes through {@link isoToDate}, so a
 * timestamp or a partial date is rejected rather than silently coerced.
 */
export const formatCalendarDate = (value: string, pattern = 'MMM d, yyyy'): string => {
  const parsed = isoToDate(value);
  return parsed ? format(parsed, pattern) : value;
};

/**
 * The calendar date currently in effect in a zone, as `YYYY-MM-DD`.
 *
 * Use for default values on new date fields, so "today" means the clinic's
 * today rather than the browser's.
 */
export const todayInZone = (timeZone: string): string => {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    day: '2-digit',
    year: 'numeric',
    month: '2-digit',
  }).formatToParts(new Date());

  const get = (type: 'year' | 'month' | 'day') => parts.find((part) => part.type === type)?.value ?? '';

  return `${get('year')}-${get('month')}-${get('day')}`;
};
